import {
  Grid2,
  Typography,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  Box,
} from "@mui/material";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import FilterListIcon from "@mui/icons-material/FilterList";
import DashboardLayout from "../../../layoutcontainers/DashboardLayout";
import CustomCalendar from "./components/HolidayWeekoffCalendar";

import MonthlyAttendanceDetail from "./components/MonthlyAttendanceDetail";

import PayrollSummary from "./components/PayrollSummary";
import DeviceHealth from "./components/DeviceHealth";
import OvertimeTracking from "./components/OvertimeTracking";
import EmployeeDemographics from "./components/EmployeeDemographics";
import ShiftMetrics from "./components/ShiftMetrics";
import DrillDownDialog from "./components/DrillDownDialog";
import { useEffect, useState, useRef, useCallback } from "react";
import BASEURL from "../../Utils/Baseurl";
import axios from "axios";
import { useLoading } from "../../../context/loading";

const POLL_INTERVAL_MS = 30000; // 30 seconds for real-time updates

export default function DashboardStats() {
  const [data, setData] = useState();
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  // Branch-level access control
  const _rawUserData = JSON.parse(sessionStorage.getItem("user") || "{}");
  const _userData = Array.isArray(_rawUserData) ? _rawUserData[0] : _rawUserData;
  const _isBranchAdmin = _userData?.is_branch_admin || false;
  const _userBranch = _userData?.branch || "";

  const [branch, setBranch] = useState(_isBranchAdmin ? _userBranch : "");
  const [branchList, setBranchList] = useState([]);
  const { setCirleLoading } = useLoading();
  const pollRef = useRef(null);

  // Derive month/year from selected date for monthly queries
  const selectedDate = new Date(date);
  const month = selectedDate.getMonth() + 1;
  const year = selectedDate.getFullYear();

  const gradients = [
    "linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)",
    "linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)",
    "linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)",
  ];

  const stats = [
    {
      label: "Total Employees Count",
      value: data?.attendanceSummary?.totalEmployees,
      icon: <PeopleAltIcon />,
      drillType: "all",
    },
    {
      label: "Employees Checked In",
      value: data?.attendanceSummary?.checkedInToday,
      icon: <WorkHistoryIcon />,
      drillType: "checkedIn",
    },
    {
      label: "Employees Not Checked In",
      value: data?.attendanceSummary?.notCheckedInToday,
      icon: <CalendarMonthIcon />,
      drillType: "notCheckedIn",
    },
  ];

  const iconColors = ["#1565C0", "#EF6C00", "#2E7D32", "#6A1B9A", "#F9A825"];

  // ---- Drill-Down State ----
  const [drillDown, setDrillDown] = useState({
    open: false,
    title: "",
    subtitle: "",
    columns: [],
    rows: [],
    loading: false,
  });

  const handleDrillDownClose = () =>
    setDrillDown((prev) => ({ ...prev, open: false }));

  const fetchDrillDown = async (query, variables = {}) => {
    const res = await axios.post(BASEURL, { query, variables });
    return res?.data?.data;
  };

  const formatCurrency = (v) =>
    v != null ? `₹${Number(v).toLocaleString("en-IN")}` : "-";

  // ---- Drill-Down Handlers ----

  const handleStatCardClick = async (type) => {
    const titles = {
      all: "All Employees",
      checkedIn: "Employees Checked In",
      notCheckedIn: "Employees Not Checked In",
    };
    const cols = [
      { key: "empid", label: "Emp ID" },
      { key: "employeeName", label: "Name" },
      { key: "branch", label: "Branch" },
      { key: "shift", label: "Shift" },
      { key: "checkIn", label: "Check In" },
      { key: "checkOut", label: "Check Out" },
      { key: "totalWorkingHours", label: "Working Hrs" },
      { key: "status", label: "Status" },
    ];
    setDrillDown({
      open: true,
      title: titles[type],
      subtitle: `Date: ${date}${branch ? ` | Branch: ${branch}` : ""}`,
      columns: cols,
      rows: [],
      loading: true,
    });
    try {
      const vars = { date };
      if (branch) vars.branch = branch;
      const d = await fetchDrillDown(
        `query($date: String!${branch ? ", $branch: String" : ""}) {
          dailyAttendanceDetail(date: $date${branch ? ", branch: $branch" : ""}) {
            empid employeeName branch shift checkIn checkOut totalWorkingHours overtime status
          }
        }`,
        vars
      );
      let rows = d?.dailyAttendanceDetail || [];
      if (type === "checkedIn") rows = rows.filter((r) => r.status === "Present");
      if (type === "notCheckedIn") rows = rows.filter((r) => r.status === "Absent");
      setDrillDown((prev) => ({ ...prev, rows, loading: false }));
    } catch {
      setDrillDown((prev) => ({ ...prev, rows: [], loading: false }));
    }
  };

  const handleMonthlyDayClick = async (clickedDate) => {
    const cols = [
      { key: "empid", label: "Emp ID" },
      { key: "employeeName", label: "Name" },
      { key: "branch", label: "Branch" },
      { key: "shift", label: "Shift" },
      { key: "checkIn", label: "Check In" },
      { key: "checkOut", label: "Check Out" },
      { key: "totalWorkingHours", label: "Working Hrs" },
      { key: "status", label: "Status" },
    ];
    setDrillDown({
      open: true,
      title: `Attendance — ${clickedDate}`,
      subtitle: branch ? `Branch: ${branch}` : "All Branches",
      columns: cols,
      rows: [],
      loading: true,
    });
    try {
      const vars = { date: clickedDate };
      if (branch) vars.branch = branch;
      const d = await fetchDrillDown(
        `query($date: String!${branch ? ", $branch: String" : ""}) {
          dailyAttendanceDetail(date: $date${branch ? ", branch: $branch" : ""}) {
            empid employeeName branch shift checkIn checkOut totalWorkingHours overtime status
          }
        }`,
        vars
      );
      setDrillDown((prev) => ({
        ...prev,
        rows: d?.dailyAttendanceDetail || [],
        loading: false,
      }));
    } catch {
      setDrillDown((prev) => ({ ...prev, rows: [], loading: false }));
    }
  };

  const handlePayrollDrillDown = async (clickedBranch) => {
    const filterBranch = clickedBranch || branch;
    const cols = [
      { key: "empid", label: "Emp ID" },
      { key: "employeeName", label: "Name" },
      { key: "branch", label: "Branch" },
      { key: "salaryDays", label: "Salary Days" },
      { key: "absentDays", label: "Absent Days" },
      { key: "grossPay", label: "Gross Pay", format: formatCurrency },
      { key: "netPay", label: "Net Pay", format: formatCurrency },
      { key: "shiftPaiseDays", label: "SP Days" },
      { key: "shiftPaiseAmount", label: "Shift Paisa", format: formatCurrency },
      { key: "otHours", label: "OT Hours" },
      { key: "otAmount", label: "OT Amount", format: formatCurrency },
    ];
    setDrillDown({
      open: true,
      title: "Payroll Details",
      subtitle: `${filterBranch ? `Branch: ${filterBranch}` : "All Branches"} | ${month}/${year}`,
      columns: cols,
      rows: [],
      loading: true,
    });
    try {
      const vars = { month, year };
      if (filterBranch) vars.branch = filterBranch;
      const d = await fetchDrillDown(
        `query($month: Int!, $year: Int!${filterBranch ? ", $branch: String" : ""}) {
          payrollDetail(month: $month, year: $year${filterBranch ? ", branch: $branch" : ""}) {
            empid employeeName branch salaryDays absentDays grossPay netPay shiftPaiseDays shiftPaiseAmount otHours otAmount
          }
        }`,
        vars
      );
      setDrillDown((prev) => ({
        ...prev,
        rows: d?.payrollDetail || [],
        loading: false,
      }));
    } catch {
      setDrillDown((prev) => ({ ...prev, rows: [], loading: false }));
    }
  };

  const handleOvertimeDrillDown = async (clickedBranch) => {
    const filterBranch = clickedBranch || branch;
    const cols = [
      { key: "empid", label: "Emp ID" },
      { key: "employeeName", label: "Name" },
      { key: "branch", label: "Branch" },
      { key: "otHours", label: "OT Hours" },
      { key: "otAmount", label: "OT Amount", format: formatCurrency },
      { key: "shiftPaiseDays", label: "SP Days" },
      { key: "shiftPaiseAmount", label: "Shift Paisa", format: formatCurrency },
    ];
    setDrillDown({
      open: true,
      title: "Overtime & Shift Paisa Details",
      subtitle: `${filterBranch ? `Branch: ${filterBranch}` : "All Branches"} | ${month}/${year}`,
      columns: cols,
      rows: [],
      loading: true,
    });
    try {
      const vars = { month, year };
      if (filterBranch) vars.branch = filterBranch;
      const d = await fetchDrillDown(
        `query($month: Int!, $year: Int!${filterBranch ? ", $branch: String" : ""}) {
          overtimeDetail(month: $month, year: $year${filterBranch ? ", branch: $branch" : ""}) {
            empid employeeName branch otHours otAmount shiftPaiseDays shiftPaiseAmount
          }
        }`,
        vars
      );
      setDrillDown((prev) => ({
        ...prev,
        rows: d?.overtimeDetail || [],
        loading: false,
      }));
    } catch {
      setDrillDown((prev) => ({ ...prev, rows: [], loading: false }));
    }
  };

  const handleDemographicSliceClick = async (dimension, value) => {
    const dimLabels = {
      branch: "Branch",
      employeeType: "Employee Type",
      category: "Category",
      gender: "Gender",
    };
    const cols = [
      { key: "empid", label: "Emp ID" },
      { key: "employeeName", label: "Name" },
      { key: "branch", label: "Branch" },
      { key: "employeeType", label: "Type" },
      { key: "category", label: "Category" },
      { key: "gender", label: "Gender" },
      { key: "dateOfJoining", label: "DOJ" },
      { key: "shift", label: "Shift" },
    ];
    setDrillDown({
      open: true,
      title: `Employees — ${dimLabels[dimension] || dimension}: ${value}`,
      subtitle: branch ? `Dashboard branch filter: ${branch}` : "",
      columns: cols,
      rows: [],
      loading: true,
    });
    try {
      const vars = {};
      // Map dimension to the GraphQL arg name
      if (dimension === "branch") vars.branch = value;
      else if (dimension === "employeeType") vars.employeeType = value;
      else if (dimension === "category") vars.category = value;
      else if (dimension === "gender") vars.gender = value;
      // Also apply dashboard branch filter if set
      if (branch && dimension !== "branch") vars.branch = branch;

      const argParts = [];
      const paramParts = [];
      if (vars.branch) { argParts.push("$branch: String"); paramParts.push("branch: $branch"); }
      if (vars.employeeType) { argParts.push("$employeeType: String"); paramParts.push("employeeType: $employeeType"); }
      if (vars.category) { argParts.push("$category: String"); paramParts.push("category: $category"); }
      if (vars.gender) { argParts.push("$gender: String"); paramParts.push("gender: $gender"); }

      const d = await fetchDrillDown(
        `query(${argParts.join(", ")}) {
          employeeListByFilter(${paramParts.join(", ")}) {
            empid employeeName branch employeeType category gender dateOfJoining shift
          }
        }`,
        vars
      );
      setDrillDown((prev) => ({
        ...prev,
        rows: d?.employeeListByFilter || [],
        loading: false,
      }));
    } catch {
      setDrillDown((prev) => ({ ...prev, rows: [], loading: false }));
    }
  };

  const handleShiftClick = async (shiftName) => {
    const cols = [
      { key: "empid", label: "Emp ID" },
      { key: "employeeName", label: "Name" },
      { key: "branch", label: "Branch" },
      { key: "checkIn", label: "Check In" },
      { key: "checkOut", label: "Check Out" },
      { key: "totalWorkingHours", label: "Working Hrs" },
      { key: "status", label: "Status" },
    ];
    setDrillDown({
      open: true,
      title: `Shift: ${shiftName}`,
      subtitle: `Date: ${date}${branch ? ` | Branch: ${branch}` : ""}`,
      columns: cols,
      rows: [],
      loading: true,
    });
    try {
      const vars = { date, shiftName };
      if (branch) vars.branch = branch;
      const d = await fetchDrillDown(
        `query($date: String!, $shiftName: String!${branch ? ", $branch: String" : ""}) {
          shiftAttendanceDetail(date: $date, shiftName: $shiftName${branch ? ", branch: $branch" : ""}) {
            empid employeeName branch checkIn checkOut totalWorkingHours status
          }
        }`,
        vars
      );
      setDrillDown((prev) => ({
        ...prev,
        rows: d?.shiftAttendanceDetail || [],
        loading: false,
      }));
    } catch {
      setDrillDown((prev) => ({ ...prev, rows: [], loading: false }));
    }
  };

  // Populate branch list from branchwiseEmployeeCount data
  useEffect(() => {
    if (data?.branchwiseEmployeeCount) {
      const branches = data.branchwiseEmployeeCount
        .map((b) => b.branch)
        .filter((b) => b && b.trim());
      setBranchList(branches);
    }
  }, [data?.branchwiseEmployeeCount]);

  useEffect(() => {
    if (_isBranchAdmin && !branch) return;
    getDashboardData();

    // Set up polling for real-time updates (only for today's date)
    const isToday = date === new Date().toISOString().split("T")[0];
    if (isToday) {
      pollRef.current = setInterval(() => {
        getDashboardData(true); // silent refresh (no loading spinner)
      }, POLL_INTERVAL_MS);
    }

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [date, branch, _isBranchAdmin]);

  const getDashboardData = async (silent = false) => {
    if (!silent) setCirleLoading(true);
    // Build branch argument string — only include if branch is selected
    const branchArgAfter = branch ? `, branch: $branch` : "";  // use after other args
    const branchArgOnly = branch ? `(branch: $branch)` : "";   // use when only arg
    const branchVar = branch ? ", $branch: String" : "";

    const query = `
    query DashboardQuery($date: String!, $month: Int!, $year: Int!${branchVar}){
  branchwiseEmployeeCount {
    branch
    totalEmployees
  }
  attendanceSummary(date: $date${branchArgAfter}) {
    totalEmployees
    checkedInToday
    notCheckedInToday
  }
  currentMonthHolidays {
    event
    eventDate
  }
  currentWeekoffDays {
    weekoffDays
  }
  monthlyAttendanceOverview(month: $month, year: $year${branchArgAfter}) {
    totalPresent
    totalAbsent
    totalEmployees
    workingDaysSoFar
    dailyBreakdown {
      date
      present
      absent
      total
    }
  }
  payrollSummary(month: $month, year: $year${branchArgAfter}) {
    totalGrossPay
    totalNetPay
    totalDeductions
    totalShiftPaisa
    employeeCount
    branchWise {
      branch
      grossPay
      netPay
      shiftPaisaAmount
      headcount
    }
  }
  deviceHealthSummary${branchArgOnly} {
    totalDevices
    onlineCount
    offlineCount
    devices {
      deviceName
      branch
      direction
      isActive
      lastSeen
      isOnline
    }
  }
  overtimeSummary(month: $month, year: $year${branchArgAfter}) {
    totalOtHours
    totalOtCost
    totalShiftPaisaDays
    totalShiftPaisaAmount
    branchWise {
      branch
      otHours
      otCost
      shiftPaisaDays
      shiftPaisaAmount
      employeeCount
    }
  }
  employeeDemographics${branchArgOnly} {
    total
    byBranch { name count }
    byEmployeeType { name count }
    byCategory { name count }
    byGender { name count }
  }
  shiftWiseMetrics(date: $date${branchArgAfter}) {
    shiftName
    startTime
    endTime
    totalEmployees
    checkedIn
    notCheckedIn
    attendancePercentage
  }
}
    `;

    const variables = {
      date: date,
      month: month,
      year: year,
    };
    if (branch) {
      variables.branch = branch;
    }

    try {
      const response = await axios.post(BASEURL, {
        query: query,
        variables: variables,
      });
      setData(response?.data?.data);
    } catch (error) {
      // silently handle error
    } finally {
      setCirleLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <Grid2 container spacing={2}>
        {/* Branch Filter Row */}
        <Grid2 size={{ xs: 12 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              justifyContent: "flex-end",
            }}
          >
            <FilterListIcon sx={{ color: "#666", fontSize: 20 }} />
            <Typography sx={{ fontSize: "13px", color: "#666", fontWeight: 500 }}>
              Branch:
            </Typography>
            <FormControl size="small">
              <Select
                value={branch}
                onChange={(e) => !_isBranchAdmin && setBranch(e.target.value)}
                disabled={_isBranchAdmin}
                displayEmpty
                sx={{
                  minWidth: 180,
                  fontSize: "13px",
                  borderRadius: "10px",
                  backgroundColor: "#fff",
                  "& .MuiSelect-select": { py: 1, px: 1.5 },
                  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                }}
              >
                <MenuItem value="">
                  <em>All Branches</em>
                </MenuItem>
                {branchList.map((b) => (
                  <MenuItem key={b} value={b}>
                    {b}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid2>

        {stats.map((stat, index) => (
          <Grid2 size={{ xs: 12, sm: 6, md: 4 }} key={index}>
            <div
              onClick={() => handleStatCardClick(stat.drillType)}
              style={{
                background: gradients[index % gradients.length],
                borderRadius: "18px",
                padding: "22px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                height: "100px",
                width: "100%",
                boxShadow: "0 3px 10px rgba(0,0,0,0.1)",
                border: "1px solid rgba(255,255,255,0.3)",
                transition: "all 0.3s ease",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 3px 10px rgba(0,0,0,0.1)";
              }}
            >
              <div>
                <Typography
                  sx={{
                    fontSize: "15px",
                    fontWeight: 600,
                    color: "#333",
                  }}
                >
                  {stat.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "30px",
                    fontWeight: "bold",
                    marginTop: "8px",
                    color: iconColors[index % iconColors.length],
                  }}
                >
                  {stat.value}
                </Typography>
              </div>

              <IconButton
                sx={{
                  backgroundColor: "white",
                  height: "55px",
                  width: "55px",
                  color: iconColors[index % iconColors.length],
                  boxShadow: "0 3px 10px rgba(0,0,0,0.1)",
                }}
              >
                {stat.icon}
              </IconButton>
            </div>
          </Grid2>
        ))}

        {/* Row 2: Calendar + Monthly Attendance */}
        <Grid2 size={{ xs: 12, md: 5 }}>
          <Tooltip title="Click a date to filter dashboard data" arrow>
            <div>
              <CustomCalendar data={data} setDate={setDate} date={date} />
            </div>
          </Tooltip>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 7 }}>
          <MonthlyAttendanceDetail data={data} onDayClick={handleMonthlyDayClick} />
        </Grid2>

        {/* Row 4: Payroll Summary + Overtime & Shift Paisa */}
        <Grid2 size={{ xs: 12, md: 6 }}>
          <PayrollSummary data={data} onDrillDown={handlePayrollDrillDown} />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <OvertimeTracking data={data} onDrillDown={handleOvertimeDrillDown} />
        </Grid2>

        {/* Row 5: Employee Demographics + Shift-wise Metrics */}
        <Grid2 size={{ xs: 12, md: 6 }}>
          <EmployeeDemographics data={data} onSliceClick={handleDemographicSliceClick} />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <ShiftMetrics data={data} onShiftClick={handleShiftClick} />
        </Grid2>

        {/* Row 6: Device Health (full width) */}
        <Grid2 size={{ xs: 12 }}>
          <DeviceHealth data={data} />
        </Grid2>
      </Grid2>

      {/* Drill-Down Dialog */}
      <DrillDownDialog {...drillDown} onClose={handleDrillDownClose} />
    </DashboardLayout>
  );
}
